import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAllTestimonials, addTestimonial } from '../../utils/firestore';
import HeroSection from '../../components/common/HeroSection';
import toast from 'react-hot-toast';
import { Form, Button, Modal, Input, Rate } from 'antd';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const testimonialsData = await getAllTestimonials();
        // Only show approved testimonials
        const approvedTestimonials = testimonialsData.filter(t => t.approved);
        setTestimonials(approvedTestimonials);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        toast.error('Failed to load testimonials');
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  const onFinish = async (values) => {
    try {
      // Mask surname (show only first name and last initial)
      const nameParts = values.name.trim().split(' ');
      let maskedName = values.name;
      if (nameParts.length >= 2) {
        const firstName = nameParts[0];
        const lastNameInitial = nameParts[nameParts.length - 1].charAt(0);
        maskedName = `${firstName} ${lastNameInitial}.`;
      }
      
      // Submit testimonial (will be pending approval by default)
      await addTestimonial({
        name: maskedName,
        comment: values.comment,
        rating: values.rating,
        approved: false, // Pending approval
        createdAt: new Date()
      });
      
      toast.success('Teşekkür ederiz! Görüşünüz incelendikten sonra kısa süre içinde yayımlanacaktır.');
      setIsModalOpen(false);
      form.resetFields();
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error('Failed to submit testimonial. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Page Header */}
      <HeroSection 
        title="Danışan Yorumları" 
        subtitle="Sağlığını dönüştürenlerden duyun" 
      />

      <div className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-nihal-dark mb-4">Danışanlarım Ne Söylüyor</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Kişiye özel beslenme rehberliği ile sağlıklarını dönüştüren bireylerden gerçek hikayeler.
            </p>
            <div className="mt-8">
              <Button 
                type="primary" 
                size="large"
                onClick={showModal}
              >
                Bir Referans Bırakın
              </Button>
            </div>
          </div>

          {testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-nihal-secondary rounded-lg p-6 shadow-md"
                >
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-5 h-5 ${i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-gray-700 italic mb-6">"{testimonial.comment}"</p>
                  
                  <div className="flex items-center">
                    {testimonial.photo ? (
                      <img
                        src={testimonial.photo}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover mr-4"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-nihal-primary flex items-center justify-center text-white mr-4">
                        <span className="font-bold">{testimonial.name.charAt(0)}</span>
                      </div>
                    )}
                    <div>
                      <h4 className="font-bold text-nihal-dark">{testimonial.name}</h4>
                      <p className="text-sm text-gray-600">{testimonial.dateOfTestimonial ? new Date(testimonial.dateOfTestimonial.toDate()).toLocaleDateString() : ''}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-bold text-nihal-dark mb-4">Henüz yorum bulunamadı</h3>
              <p className="text-gray-600">Başarı hikayenizi paylaşan ilk kişi olun!</p>
            </div>
          )}

          {/* Success story section */}
          
         
        </div>
      </div>

      {/* Testimonial Submission Modal */}
      <Modal
        title="Yorum Bırakın"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          requiredMark={true}
        >
          <Form.Item
            name="name"
            label="Adınız Soyadınız"
            rules={[{ required: true, message: 'Lütfen adınızı ve soyadınızı girin!' }]}
          >
            <Input placeholder="Adınızı soyadınızı giriniz" />
          </Form.Item>
          
          <Form.Item
            name="rating"
            label="Skor"
            initialValue={5}
            rules={[{ required: true, message: 'Lütfen skorlama yapınız!' }]}
          >
            <Rate />
          </Form.Item>
          
          <Form.Item
            name="comment"
            label="Yorumunuz"
            rules={[{ required: true, message: 'Lütfen deneyiminizi paylaşın!' }]}
          >
            <Input.TextArea rows={4} placeholder="Deneyiminizi paylaşın..." />
          </Form.Item>
          
          <Form.Item>
            <div className="flex justify-end space-x-3">
              <Button onClick={handleCancel}>
                İptal
              </Button>
              <Button type="primary" htmlType="submit">
                Yorumunu Gönder
              </Button>
            </div>
          </Form.Item>
          
          <div className="text-sm text-gray-500 mt-2">
            <p>Not: Referanslar onay sürecine tabidir.</p>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default Testimonials;